import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import * as R from 'ramda';
import accountTypes from '@app/home/accountTypes';
import CenterMessage from '@app/components/common/CenterMessage';
import {
    Card,
    Typography,
    Tabs,
    ConfigProvider,
    Flex,
    theme,
    Spin,
} from '@app/ui';
import { Pie, PieConfig } from '@ant-design/charts';
import { colors10 } from '@app/constants/theme';
import { translateMediaTypeLabel } from '@app/utils/getMediaLabelsById';

const MediaValueByAcctype = ({
    mediaValueByAccType,
    selectAccType,
    accTypeSelected,
}) => {
    const { t } = useTranslation('home');
    const [hoveredTab, setHoveredTab] = React.useState(null);
    const [activeKey, setActiveKey] = React.useState(accountTypes.mediaType);

    const onChange = key => {
        selectAccType(key);
        setActiveKey(key);
    };

    const translateLabel = React.useCallback(
        (name: string) => translateMediaTypeLabel(name, t),
        [t],
    );

    const _getMediaValue = type => {
        const { values } = mediaValueByAccType[type];

        const sum = R.sum(R.map(R.prop('value'), values));

        const transformedData = values.map(v => ({
            type: v.name,
            value: v.value / sum,
        }));

        const labels = values.map(v => v.name);

        return { labels, data: transformedData };
    };

    const isMVByAccTypeDataLoading =
        mediaValueByAccType[accTypeSelected].isLoading;
    const hasMVByAccTypeError = Boolean(
        mediaValueByAccType[accTypeSelected].hasError,
    );

    const inputData = _getMediaValue(accTypeSelected);
    const hasData = inputData.data.length !== 0;

    const formattedData = useMemo(
        () =>
            inputData.data.map(d => ({
                type: translateLabel(d.type),
                value: Math.round(d.value * 10000) / 10000,
            })),
        [inputData.data, translateLabel],
    );

    const config: PieConfig = {
        data: formattedData,
        angleField: 'value',
        colorField: 'type',
        innerRadius: 0.65,
        label: {
            text: ({ value }) =>
                value * 100 > 1 ? `${(value * 100).toFixed(2)}%` : '',
            style: {
                fill: 'white',
                fontWeight: 400,
                fontSize: 16,
            },
        },
        legend: false,
        interactions: [{ type: 'element-active' }],
        scale: {
            color: {
                range: colors10,
            },
        },
        // Remove theme if colors are now set via 'color' property
    };

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const items = useMemo(() => {
        return [
            {
                key: accountTypes.mediaType,
                nameKey: 'mediaValueByAccType.accountTypes.mediaType',
                icon: 'fa-light fa-image',
            },
            {
                key: accountTypes.social,
                nameKey: 'mediaValueByAccType.accountTypes.social',
                icon: 'fa-light fa-thumbs-up',
            },
            {
                key: accountTypes.broadcast,
                nameKey: 'mediaValueByAccType.accountTypes.broadcast',
                icon: 'fa-light fa-tv-retro',
            },
            {
                key: accountTypes.owned,
                nameKey: 'mediaValueByAccType.accountTypes.ownedChannels',
                icon: 'fa-light fa-globe',
                disabled: true,
            },
        ]
            .map(({ key, nameKey, icon, disabled }) => {
                const isActive = activeKey === key;
                const isHovered = hoveredTab === key;

                const color = disabled
                    ? '#cacec6'
                    : isActive || isHovered
                      ? colorPrimary
                      : '#515252';

                return {
                    key,
                    label: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                            onMouseEnter={() => setHoveredTab(key)}
                            onMouseLeave={() => setHoveredTab(null)}
                        >
                            <i className={icon} />
                            <Typography.Text
                                style={{
                                    margin: 0,
                                    fontWeight: 400,
                                    color,
                                }}
                            >
                                {t(nameKey)}
                            </Typography.Text>
                        </div>
                    ),
                    disabled,
                };
            })
            .filter(Boolean);
    }, [activeKey, hoveredTab, t]);

    const getAccTypeSelectedLabel = () => {
        switch (accTypeSelected) {
            case accountTypes.mediaType:
                return t('mediaValueByAccType.accountTypes.mediaType');
            case accountTypes.social:
                return t('mediaValueByAccType.accountTypes.social');
            case accountTypes.broadcast:
                return t('mediaValueByAccType.accountTypes.broadcast');
            case accountTypes.owned:
                return t('mediaValueByAccType.accountTypes.ownedChannels');
            default:
                return '';
        }
    };

    const pieHeight = 348 - Math.floor(inputData.labels.length / 4) * 32;

    return (
        <React.Fragment>
            <ConfigProvider
                theme={{
                    components: {
                        Card: {
                            bodyPadding: 0,
                        },
                    },
                }}
            >
                <Card style={{ zIndex: 0, height: '100%' }}>
                    <Tabs
                        defaultActiveKey="1"
                        accessKey={activeKey}
                        items={items}
                        onChange={onChange}
                        centered
                        style={{
                            padding: '16px 12px 0 12px',
                        }}
                    />

                    <Flex
                        justify="center"
                        align="center"
                        style={{
                            flexDirection: 'column',
                        }}
                    >
                        <Flex justify="center">
                            <Typography.Text
                                style={{
                                    fontWeight: 700,
                                    color: '#5E6066',
                                    fontSize: 16,
                                }}
                            >
                                {t('mediaValueByAccType.mediaValueBy', {
                                    type: getAccTypeSelectedLabel(),
                                })}
                            </Typography.Text>
                        </Flex>
                        <Flex
                            justify="center"
                            style={{
                                width: pieHeight,
                                height: pieHeight,
                                flexDirection: 'column',
                                marginTop: 12,
                            }}
                        >
                            {!isMVByAccTypeDataLoading &&
                                !hasMVByAccTypeError &&
                                hasData && (
                                    <>
                                        <Pie {...config} style={{ flex: 0 }} />
                                    </>
                                )}
                            {isMVByAccTypeDataLoading && <Spin size="large" />}
                            {hasMVByAccTypeError &&
                                !isMVByAccTypeDataLoading && (
                                    <CenterMessage
                                        text={t('overview.loadError')}
                                    />
                                )}
                            {!hasData &&
                                !hasMVByAccTypeError &&
                                !isMVByAccTypeDataLoading && (
                                    <CenterMessage
                                        text={t(
                                            'mediaValueByAccType.noDataAvailable',
                                        )}
                                    />
                                )}
                        </Flex>
                        <Flex
                            style={{
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 12,
                                flexWrap: 'wrap',
                                maxWidth: 400,
                                rowGap: 0,
                            }}
                        >
                            {!hasMVByAccTypeError &&
                                inputData.labels.map((label, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            marginTop: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor:
                                                    colors10[index],
                                            }}
                                        />
                                        <Typography.Text
                                            style={{
                                                margin: 0,
                                                color: '#5E6066',
                                                fontWeight: 400,
                                                fontSize: 16,
                                            }}
                                        >
                                            {translateLabel(label)}
                                        </Typography.Text>
                                    </div>
                                ))}
                        </Flex>
                    </Flex>
                </Card>
            </ConfigProvider>
        </React.Fragment>
    );
};

MediaValueByAcctype.propTypes = {
    mediaValueByAccType: PropTypes.object.isRequired,
    accTypeSelected: PropTypes.string.isRequired,
    selectAccType: PropTypes.func.isRequired,
};

export default MediaValueByAcctype;
